import { ComponentProps, useCallback, useMemo } from 'react';
import { CatalogEntry, LearningPath } from 'types';
import { v4 as uuidv4 } from 'uuid';
import DurationIcon from 'img/icons/estimated-duration.svg';
import LabIcon from 'img/icons/lab-2.svg';
import LevelIcon from 'img/icons/level.svg';
import QuizzesIcon from 'img/icons/quizzes-icon.svg';
import VideoIcon from 'img/icons/video.svg';
import { OfferingCard } from '@ole-ui/ole-ui-components';
import SkillPathOfferingIcon from 'img/icons/skill-path-offering-icon.svg';
import { BodyRowType, IconLabel, OfferingTypes } from '../../utils/types';
import useOfferingInfo from './useOfferingInfo';
import offeringTypeRegistry from '../../configs';

const useBodyRowIcons = (
  entry: CatalogEntry,
  offeringType: OfferingTypes,
  hasProgress: boolean,
  learningPath?: LearningPath,
) => {
  const {
    level,
    skillsPathOfferings,
    totalDuration,
    totalLabs,
    totalQuizzes,
    totalVideoDuration,
  } = useOfferingInfo(entry, learningPath);

  const metadataBasicLabels = useMemo(
    () => ({
      [IconLabel.Duration]: {
        path: DurationIcon,
        label: totalDuration,
      },
      [IconLabel.Labs]: {
        path: LabIcon,
        label: totalLabs,
      },
      [IconLabel.Level]: {
        path: LevelIcon,
        label: level,
      },
      [IconLabel.Offerings]: {
        path: SkillPathOfferingIcon,
        label: skillsPathOfferings,
      },
      [IconLabel.Quizzes]: {
        path: QuizzesIcon,
        label: totalQuizzes,
      },
      [IconLabel.Videos]: {
        path: VideoIcon,
        label: totalVideoDuration,
      },
    }),
    [
      level,
      skillsPathOfferings,
      totalDuration,
      totalLabs,
      totalQuizzes,
      totalVideoDuration,
    ],
  );

  const buildIconRows = useCallback(() => {
    const config = offeringTypeRegistry[offeringType];
    const offeringBodyIconRows =
      config?.iconRowsLayout?.({ hasProgress }) ?? [];

    return offeringBodyIconRows.reduce((currentOfferingBodyIconRows, row) => {
      const formatedRow = {
        icons: row?.reduce(
          (
            prevIconArray: ComponentProps<typeof OfferingCard.BodyRow>['icons'],
            iconLabel,
          ) =>
            metadataBasicLabels[iconLabel].label
              ? [...prevIconArray, metadataBasicLabels[iconLabel]]
              : prevIconArray,
          [],
        ),
        key: uuidv4(),
        type: BodyRowType.Icons,
      } as ComponentProps<typeof OfferingCard.BodyRow>;

      return formatedRow.icons.length
        ? [...currentOfferingBodyIconRows, formatedRow]
        : currentOfferingBodyIconRows;
    }, [] as ComponentProps<typeof OfferingCard.BodyRow>[]);
  }, [hasProgress, metadataBasicLabels, offeringType]);

  return { buildIconRows };
};

export default useBodyRowIcons;
